import React from 'react';
import { CreditCard, Star, Fuel, Route, IdCard, Wrench, Shield, CheckCircle2, Ban, Lock, Warehouse } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { VEHICLES } from '../../data/vehicles';
import { describeVehicleAccessibility, VEHICLE_LICENSE_LABELS, getLicenseLabel } from '../../data/vehicleAccessibility';
import { localize } from '../../data/i18nData';
import useGameStore from '../../store/gameStore';
import { garageExpansionRequired } from '../../store/slices/fleetSlice';

export function LeaseTab({
  treasury,
  leasedVehicles,
  monthlyLeasingCost,
  leaseDuration,
  setLeaseDuration,
  setLeaseConfirmTarget,
  canUnlock,
  phase = 1,
  totalFleetSize = 0,
}) {
  const { t } = useTranslation();
  // 14/06/2026 — garage de base = 2 places : le 3e camion (même en leasing)
  // exige l'Agrandissement dépôt (x2).
  const garageBlocked = useGameStore(garageExpansionRequired);
  // Phase-based cap on leased vehicles. Phase 3+ unlimited.
  const LEASING_CAP = { 1: 5, 2: 10 };
  const cap = LEASING_CAP[phase];
  const capReached = cap != null && leasedVehicles >= cap;
  const leasedRatio = totalFleetSize > 0 ? leasedVehicles / totalFleetSize : 0;
  const ratioWarn = totalFleetSize >= 5 && leasedRatio > 0.70 && phase < 3;
  return (
    <div className="space-y-4">
      {garageBlocked && (
        <div className="bg-warn/10 border border-warn/40 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid="garage-capacity-banner-lease">
          <Warehouse className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-warn">{t('fleet_garage_full_title')}</p>
            <p className="text-[11px] text-slate-300 mt-0.5">{t('fleet_garage_full_body')}</p>
          </div>
        </div>
      )}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-4 text-white">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-5 h-5" />
          <h3 className="font-bold text-sm">{t('leasing_title')}</h3>
        </div>
        <p className="text-xs text-purple-100">
          {t('leasing_description')}
        </p>
        <div className="flex items-center gap-4 mt-3">
          <div>
            <p className="text-[10px] text-purple-200">{t('chosen_duration')}</p>
            <div className="flex gap-1 mt-1">
              {[24, 36, 48].map(d => (
                <button
                  key={d}
                  onClick={() => setLeaseDuration(d)}
                  className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                    leaseDuration === d ? 'bg-ink-800 text-purple-400' : 'bg-purple-500/100/50 text-purple-100 hover:bg-purple-500/100'
                  }`}
                  data-testid={`lease-duration-${d}`}
                >
                  {d} mois
                </button>
              ))}
            </div>
          </div>
          {leasedVehicles > 0 && (
            <div className="ml-auto text-right">
              <p className="text-[10px] text-purple-200">{t('current_payments')}</p>
              <p className="text-lg font-bold">{formatMoney(monthlyLeasingCost)}/m</p>
              {cap != null && (
                <p className="text-[10px] text-purple-100 mt-0.5" data-testid="lease-cap-indicator">
                  {leasedVehicles}/{cap} véhicules — Phase {phase}
                </p>
              )}
            </div>
          )}
        </div>
        {(capReached || ratioWarn) && (
          <div className="mt-3 bg-rose-900/40 border border-rose-400/50 rounded-md p-2 text-[11px] text-rose-100" data-testid="lease-warn-banner">
            {capReached && (
              <p className="flex items-center gap-1">
                <span className="font-bold">⚠</span> Plafond leasing atteint : {cap} véhicules max en Phase {phase}. Achète ton prochain véhicule ou passe en phase supérieure pour continuer.
              </p>
            )}
            {!capReached && ratioWarn && (
              <p>
                <span className="font-bold">⚠</span> {Math.round(leasedRatio * 100)}% de ta flotte est en leasing. Au-delà de 70%, ta réputation se dégrade (clients & presse jugent l'entreprise peu solide).
              </p>
            )}
          </div>
        )}
      </div>

      {[1, 2, 3, 4, 5].map(tier => {
        const tierVehicles = VEHICLES.filter(v => v.tier === tier);
        return (
          <div key={tier}>
            <h3 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1">
              <Star className="w-4 h-4 text-warn" /> Tier {tier}
            </h3>
            <div className="space-y-2">
              {tierVehicles.map(rawV => {
                const v = localize('vehicle', rawV);
                const unlocked = canUnlock(rawV);
                const deposit = Math.round(v.price * 0.10);
                const monthly = Math.round((v.price * 0.90) / leaseDuration * 1.08);
                const canDeposit = treasury >= deposit;
                return (
                  <div key={v.id} className={`bg-ink-800 rounded-lg border p-4 ${unlocked ? 'border-ink-600' : 'border-ink-600 opacity-60'}`} data-testid={`lease-vehicle-${v.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold text-slate-100">{v.brand} {v.model}</h4>
                        <p className="text-xs text-slate-500">{v.ptac} — {v.specialties.join(', ')}</p>
                        {v.advantage && <p className="text-xs text-slate-500 mt-1">{v.advantage}</p>}
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-0.5"><Fuel className="w-3 h-3" /> {v.consumption}L/100</span>
                          <span className="flex items-center gap-0.5"><Route className="w-3 h-3" /> {v.kmPerDay} km/j</span>
                          <span className="flex items-center gap-0.5"><Wrench className="w-3 h-3" /> {formatMoney(v.maintenancePerMonth)}/m</span>
                          <span className="flex items-center gap-0.5"><Shield className="w-3 h-3" /> {formatMoney(v.insurancePerMonth)}/m</span>
                        </div>
                        {/* 04/06/2026 — Conditions d'achat affichées sur le leasing :
                            • Permis requis avec libellé complet (cohérent avec BuyTab).
                            • Bloc accessibilité contrats (repliable, mêmes infos que BuyTab).
                            • Cause précise de verrouillage (Phase X requise).
                            Demande utilisateur : "Les règles de conditions d'achats
                            doivent être aussi indiquées dans les leasings." */}
                        {(() => {
                          const lic = getLicenseLabel(v.license);
                          return (
                            <p
                              className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold"
                              style={{ color: lic.color, backgroundColor: `${lic.color}15`, borderColor: `${lic.color}40` }}
                              data-testid={`lease-license-${v.id}`}
                              title={lic.full}
                            >
                              <IdCard className="w-3 h-3" /> {lic.code}
                              <span className="text-[10px] font-normal opacity-80 ml-0.5">— {lic.full}</span>
                            </p>
                          );
                        })()}
                        {!unlocked && (
                          <p
                            className="text-[10px] mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30"
                            data-testid={`lease-locked-reason-${v.id}`}
                          >
                            <Lock className="w-3 h-3" /> Verrouillé — Phase {tier} requise
                          </p>
                        )}
                        {(() => {
                          const acc = describeVehicleAccessibility(v);
                          return (
                            <details className="mt-2 group" data-testid={`lease-vehicle-access-${v.id}`}>
                              <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors">
                                Quels contrats ce camion prend-il ?
                              </summary>
                              <div className="mt-1.5 pl-1 space-y-1">
                                <p className="text-[10px] text-slate-500 italic">{acc.hint}</p>
                                <ul className="text-[10px] space-y-0.5">
                                  {acc.accessible.map(item => (
                                    <li key={`ok-${item}`} className="flex items-start gap-1 text-emerald-300">
                                      <CheckCircle2 className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                  {acc.blocked.map(item => (
                                    <li key={`ko-${item}`} className="flex items-start gap-1 text-rose-300/70">
                                      <Ban className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </details>
                          );
                        })()}
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <p className="text-[10px] text-slate-500 line-through">{formatMoney(v.price)} {t('new_price')}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{t('deposit')} <span className="font-bold text-purple-400">{formatMoney(deposit)}</span></p>
                        <p className="text-lg font-bold text-purple-400">{formatMoney(monthly)}<span className="text-xs font-normal">/m</span></p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="text-[10px] text-slate-500">
                        {leaseDuration} mois — Rachat possible : {formatMoney(Math.round(v.price * 0.25))}
                      </div>
                      <button
                        onClick={() => setLeaseConfirmTarget(v)}
                        disabled={!unlocked || !canDeposit || garageBlocked}
                        className="text-xs bg-purple-600 text-white px-4 py-2 rounded-md font-medium disabled:opacity-40 hover:bg-purple-700 transition-colors"
                        data-testid={`lease-btn-${v.id}`}
                      >
                        {garageBlocked && unlocked ? t('fleet_garage_full_btn') : !canDeposit && unlocked ? t('insufficient_deposit') : unlocked ? t('sign_lease') : t('locked')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
